import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Calendar, BookOpen, Users, BarChart3, ArrowLeft, Mail, GraduationCap, Star, HelpCircle, CalendarDays, Youtube, FileCheck, ShoppingCart, Globe, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import AdminSchedules from './AdminSchedules';
import AdminLeads from './AdminLeads';
import AdminUsers from './AdminUsers';
import AdminEnrollments from './AdminEnrollments';
import AdminReviews from './AdminReviews';
import AdminQuestions from './AdminQuestions';
import AdminEvents from './AdminEvents';
import AdminBimtalks from './AdminBimtalks';
import AdminEnrollmentRequests from './AdminEnrollmentRequests';
import AdminCheckoutConfig from './AdminCheckoutConfig';
import AdminBimmapLeads from './AdminBimmapLeads';
import AdminConnectAccess from './AdminConnectAccess';
import AdminPrograms from './AdminPrograms';
import AdminHotmartMapping from './AdminHotmartMapping';
import AdminDeliverables from './AdminDeliverables';
import AdminClassSchedules from './AdminClassSchedules';
import AdminContentGenerator from './AdminContentGenerator';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    courses: 0,
    users: 0,
    enrollments: 0,
    leads: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
      }
    };
    if (token) fetchStats();
  }, [token]);

  // Wait for auth context to finish loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const statsCards = [
    { icon: BookOpen, label: 'Cursos', value: stats.courses?.toString() || '27', color: 'text-blue-500' },
    { icon: Users, label: 'Usuários', value: stats.users?.toString() || '0', color: 'text-purple-500' },
    { icon: GraduationCap, label: 'Matrículas', value: stats.enrollments?.toString() || '0', color: 'text-green-500' },
    { icon: Mail, label: 'Leads', value: stats.leads?.toString() || '0', color: 'text-red-500' }
  ];

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Painel Administrativo</h1>
            <p className="text-gray-400">Bem-vindo, {user?.name}</p>
          </div>
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
            className="border-zinc-700 text-white"
          >
            <ArrowLeft className="mr-2" size={18} />
            Voltar ao Site
          </Button>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">{stat.label}</p>
                      <p className="text-2xl font-bold text-white">{stat.value}</p>
                    </div>
                    <Icon className={stat.color} size={32} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="enrollments" className="w-full">
          <TabsList className="bg-zinc-900 mb-6 flex-wrap">
            <TabsTrigger value="enrollments" className="data-[state=active]:bg-red-600">
              <GraduationCap className="mr-2" size={18} />
              Matrículas
            </TabsTrigger>
            <TabsTrigger value="leads" className="data-[state=active]:bg-red-600">
              <Mail className="mr-2" size={18} />
              Leads
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-red-600">
              <Users className="mr-2" size={18} />
              Usuários
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-red-600">
              <Star className="mr-2" size={18} />
              Avaliações
            </TabsTrigger>
            <TabsTrigger value="schedules" className="data-[state=active]:bg-red-600">
              <Calendar className="mr-2" size={18} />
              Turmas
            </TabsTrigger>
            <TabsTrigger value="questions" className="data-[state=active]:bg-red-600">
              <HelpCircle className="mr-2" size={18} />
              Questões
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-red-600">
              <CalendarDays className="mr-2" size={18} />
              Eventos
            </TabsTrigger>
            <TabsTrigger value="bimtalks" className="data-[state=active]:bg-red-600">
              <Youtube className="mr-2" size={18} />
              BIMTALKS
            </TabsTrigger>
            <TabsTrigger value="enrollment-requests" className="data-[state=active]:bg-red-600" data-testid="tab-enrollment-requests">
              <FileCheck className="mr-2" size={18} />
              Matrículas c/ Desconto
            </TabsTrigger>
            <TabsTrigger value="checkout-config" className="data-[state=active]:bg-red-600" data-testid="tab-checkout-config">
              <ShoppingCart className="mr-2" size={18} />
              Checkouts Hotmart
            </TabsTrigger>
            <TabsTrigger value="bimmap-leads" className="data-[state=active]:bg-red-600" data-testid="tab-bimmap-leads">
              <Globe className="mr-2" size={18} />
              Leads BIMMap
            </TabsTrigger>
            <TabsTrigger value="connect-access" className="data-[state=active]:bg-red-600" data-testid="tab-connect-access">
              <Users className="mr-2" size={18} />
              Acessos Connect
            </TabsTrigger>
            <TabsTrigger value="programs" className="data-[state=active]:bg-red-600" data-testid="tab-programs">
              <GraduationCap className="mr-2" size={18} />
              Programas
            </TabsTrigger>
            <TabsTrigger value="hotmart-mapping" className="data-[state=active]:bg-red-600" data-testid="tab-hotmart-mapping">
              <Globe className="mr-2" size={18} />
              Mapeamento Hotmart
            </TabsTrigger>
            <TabsTrigger value="deliverables" className="data-[state=active]:bg-red-600" data-testid="tab-deliverables">
              <FileCheck className="mr-2" size={18} />
              Avaliações (IA)
            </TabsTrigger>
            <TabsTrigger value="class-schedules" className="data-[state=active]:bg-red-600" data-testid="tab-class-schedules">
              <CalendarDays className="mr-2" size={18} />
              Turmas & Cronogramas
            </TabsTrigger>
            <TabsTrigger value="content-ai" className="data-[state=active]:bg-red-600" data-testid="tab-content-ai">
              <Sparkles className="mr-2" size={18} />
              Gerador IA
            </TabsTrigger>
          </TabsList>

          <TabsContent value="enrollments">
            <AdminEnrollments />
          </TabsContent>

          <TabsContent value="leads">
            <AdminLeads />
          </TabsContent>

          <TabsContent value="users">
            <AdminUsers />
          </TabsContent>

          <TabsContent value="reviews">
            <AdminReviews />
          </TabsContent>

          <TabsContent value="schedules">
            <AdminSchedules />
          </TabsContent>

          <TabsContent value="questions">
            <AdminQuestions />
          </TabsContent>

          <TabsContent value="events">
            <AdminEvents />
          </TabsContent>

          <TabsContent value="bimtalks">
            <AdminBimtalks />
          </TabsContent>

          <TabsContent value="enrollment-requests">
            <AdminEnrollmentRequests />
          </TabsContent>

          <TabsContent value="checkout-config">
            <AdminCheckoutConfig />
          </TabsContent>

          <TabsContent value="bimmap-leads">
            <AdminBimmapLeads />
          </TabsContent>

          <TabsContent value="connect-access">
            <AdminConnectAccess />
          </TabsContent>

          <TabsContent value="programs">
            <AdminPrograms />
          </TabsContent>

          <TabsContent value="hotmart-mapping">
            <AdminHotmartMapping />
          </TabsContent>

          <TabsContent value="deliverables">
            <AdminDeliverables />
          </TabsContent>

          <TabsContent value="class-schedules">
            <AdminClassSchedules />
          </TabsContent>

          <TabsContent value="content-ai">
            <AdminContentGenerator />
          </TabsContent>
        </Tabs>

        {/* Botão Voltar ao Início */}
        <div className="text-center mt-12">
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
            className="border-zinc-700 text-white hover:bg-zinc-800"
          >
            <ArrowLeft className="mr-2" size={18} />
            Voltar ao Início
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;